import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { marker } from '@common/translate-marker';
import { ListExcelExportFile, ListExcelExportRequest } from '@common/models/common/ListExcelExport.model';
import { UserModel } from '@common/models/User.model';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from '@progress/kendo-angular-dialog';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { AddEditMotOnServiceRequestSaveModel } from 'app/tos-operations/models/AddEditMotOnServiceRequest.save.model';
import { BulkUpdateMotIdentifiersSaveModel } from 'app/tos-operations/models/BulkUpdateMotIdentifiers.save.model';
import { CreateServiceRequestSaveModel } from 'app/tos-operations/models/CreateServiceRequest.save.model';
import { MigrationExcelFileModel } from 'app/tos-operations/models/MigrationExcelFile.model';
import { RemoveMotFromServiceRequestSaveModel } from 'app/tos-operations/models/RemoveMotFromServiceRequest.save.model';
import { RemoveServiceRequestVersionShipmentSaveModel } from 'app/tos-operations/models/RemoveServiceRequestVersionShipment.save.model';
import { Observable } from 'rxjs';

export enum TosNamespace {
    ServiceRequest = 'ServiceRequest',
    AmendmentDocument = 'AmendmentDocument',
    ShipmentPool = 'ShipmentPool',
    StorageDocument = 'StorageDocument'
}

@Injectable({
    providedIn: 'root'
})
export class ServiceRequestService {
    constructor(
        private http: HttpClient,
        private modalService: DialogService,
        private translateService: TranslateService
    ) {}

    updateVesselServiceRequest(model: any): Observable<any> {
        return this.http.post<any>('api:///serviceRequest/UpdateVesselServiceRequest', model);
    }

    getEditVesselServiceRequestData(id: number): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetEditVesselServiceRequestData/${id}`);
    }

    getServiceRequestSumData(serviceRequestId: number): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetServiceRequestSumData/${serviceRequestId}`);
    }

    getServiceRequestList(filter: any): Observable<any> {
        return this.http.post('api:///ServiceRequest/GetServiceRequestList', filter);
    }

    exportServiceRequestList(body: ListExcelExportRequest): Observable<ListExcelExportFile> {
        return this.http.post<ListExcelExportFile>('api:///ServiceRequest/ExportServiceRequestList', body);
    }

    createVesselServiceRequest(body: any): Observable<any> {
        return this.http.post('api:///serviceRequest/CreateNewVesselServiceRequest', body);
    }

    addNavisItems(body: any) {
        return this.http.post('api:///serviceRequest/AddNavisItems', body);
    }

    addAttachment(model: any) {
        return this.http.post<any>(`api:///ServiceRequest/AddAttachment`, model);
    }

    removeAttachment(model: any) {
        return this.http.post<any>(`api:///ServiceRequest/RemoveAttachment`, model);
    }

    getManipulationsForSystemAndServiceRequestType(system: string, serviceRequestType: string): Observable<any> {
        const body = { system, serviceRequestType };
        return this.http.post('api:///ServiceRequest/GetManipulationsForSystemAndServiceRequestType', body);
    }

    getServiceRequest(serviceRequestId: number): Observable<any> {
        return this.http.get(`api:///ServiceRequest/GetServiceRequest/${serviceRequestId}`);
    }

    getServiceRequestMainPrintData(serviceRequestId: number): Observable<any> {
        return this.http.get(`api:///ServiceRequest/GetServiceRequestMainPrintData/${serviceRequestId}`);
    }

    getServiceRequestFullPrintData(serviceRequestId: number): Observable<any> {
        return this.http.get(`api:///ServiceRequest/GetServiceRequestFullPrintData/${serviceRequestId}`);
    }

    updateLandServiceRequest(dto: any): Observable<any> {
        return this.http.post<any>('api:///serviceRequest/UpdateLandServiceRequest', dto);
    }

    populateContainerDataOnLandSR(containerNumber: string, user: any, serviceRequestId: number): Observable<any> {
        const body = { containerId: containerNumber, user: user, serviceRequestId: serviceRequestId };
        return this.http.post(`api:///ServiceRequest/PopulateContainerDataOnLandSR/`, body);
    }

    updateDataOnServiceRequest(serviceRequestId: number): Observable<any> {
        return this.http.post<any>(`api:///serviceRequest/updateDataOnServiceRequest/${serviceRequestId}`, null);
    }

    cloneServiceRequest(serviceRequestId: number): Observable<any> {
        return this.http.post<any>(`api:///serviceRequest/cloneServiceRequest/${serviceRequestId}`, null);
    }

    deleteVesselServiceRequest(serviceRequestId: number) {
        return this.http.get<any>(`api:///serviceRequest/DeleteVesselServiceRequest/${serviceRequestId}`);
    }

    completeServiceRequestRealization(serviceRequestId: number): Observable<any> {
        return this.http.post<any>(`api:///serviceRequest/CompleteServiceRequestRealization/${serviceRequestId}`, null);
    }

    uploadContainerMigration(file: MigrationExcelFileModel): Observable<any> {
        return this.http.post<any>(`api:///migration/UploadContainerMigration/`, file);
    }

    addServiceRequestWorkOrder(serviceRequestId: number, workOrderIds: number[]) {
        return this.http.post<any>(`api:///serviceRequest/AddServiceRequestWorkOrder`, {
            serviceRequestId: serviceRequestId,
            workOrderIds: workOrderIds
        });
    }

    createTruckDeklot(serviceRequestId: number) {
        return this.http.post(`api:///ServiceRequest/CreateDeklotTruck/${serviceRequestId}`, null);
    }

    getPortsForUserOnCreateServiceRequest(): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetPortsForUserOnCreateServiceRequest/`);
    }

    getTerminalsOnServiceRequest(port: string): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetTerminalsOnServiceRequest/${port}`);
    }

    getTerminalOperatorsOnServiceRequest(terminal: string): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetTerminalOperatorsOnServiceRequest/${terminal}`);
    }

    getSecondaryTerminalsOnServiceRequest(
        terminalOperator: number,
        terminal: string,
        tosSystem: string,
        port?: string,
        isTrater?: boolean
    ): Observable<any> {
        return this.http.get<any>(
            `api:///serviceRequest/GetSecondaryTerminalsOnServiceRequest/${terminalOperator}/${terminal}/${tosSystem}/${port}/${isTrater}`
        );
    }

    getTosSystemsOnServiceRequest(terminal: string, terminalOperator: number): Observable<any> {
        return this.http.get<any>(
            `api:///serviceRequest/getTosSystemsOnServiceRequest/${terminal}/${terminalOperator}`
        );
    }

    getShipmentTypesOnServiceRequest(tosSystem: string): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetShipmentTypesOnServiceRequest/${tosSystem}`);
    }

    getAdditionalServicesOnServiceRequest(tosSystem: string): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetAdditionalServicesOnServiceRequest/${tosSystem}`);
    }

    getAdditionalServicesBasedOnManipulation(filter: any): Observable<any> {
        return this.http.post<any>(`api:///serviceRequest/GetAdditionalServicesBasedOnManipulation`, filter);
    }

    getManipulationsOnServiceRequest(filter: any): Observable<any> {
        return this.http.post<any>(`api:///serviceRequest/GetManipulationsOnServiceRequest`, filter);
    }

    getManipulationSrTypeAndMot(manipulation: string): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetManipulationSrTypeAndMot/${manipulation}`);
    }

    getVesselFromVesselVisit(vesselVisitId: number, manipulationId: string = null): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetVesselFromVesselVisit/${vesselVisitId}/${manipulationId}`);
    }

    getVoyageNumberFromVesselVisit(filter: any): Observable<any> {
        return this.http.post('api:///ServiceRequest/GetVoyageNumberFromVesselVisit', filter);
    }

    getVesselVisitsBasedOnManipulation(filter: any): Observable<any> {
        return this.http.post<any>(`api:///serviceRequest/GetVesselVisitsBasedOnManipulation`, filter);
    }

    getLoadingVesselVisits(filter: any): Observable<any> {
        return this.http.post<any>(`api:///serviceRequest/GetLoadingVesselVisits`, filter);
    }

    createServiceRequest(body: CreateServiceRequestSaveModel): Observable<any> {
        return this.http.post<any>(`api:///serviceRequest/CreateServiceRequest/`, body);
    }

    createServiceRequestFromShipmentPool(body: any): Observable<any> {
        return this.http.post<any>(`api:///serviceRequest/CreateServiceRequestFromShipmentPool/`, body);
    }

    createServiceRequestFromReleaseOrder(body: any): Observable<any> {
        return this.http.post<any>(`api:///serviceRequest/CreateServiceRequestFromReleaseOrder/`, body);
    }

    getShippingLinesAndAgents(): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetShippingLinesAndAgents/`);
    }

    addMotOnServiceRequest(body: AddEditMotOnServiceRequestSaveModel): Observable<any> {
        return this.http.post<any>(`api:///serviceRequest/AddMotOnServiceRequest/`, body);
    }

    editMotOnServiceRequest(body: AddEditMotOnServiceRequestSaveModel): Observable<any> {
        return this.http.post<any>(`api:///serviceRequest/EditMotOnServiceRequest/`, body);
    }

    bulkUpdateMotIdentifiersOnServiceRequest(body: BulkUpdateMotIdentifiersSaveModel): Observable<any> {
        return this.http.post<any>(`api:///serviceRequest/BulkUpdateMotIdentifiers/`, body);
    }

    removeMotFromServiceRequest(body: RemoveMotFromServiceRequestSaveModel): Observable<any> {
        return this.http.post<any>(`api:///serviceRequest/RemoveMotFromServiceRequest/`, body);
    }

    removeServiceRequestVersionShipment(body: RemoveServiceRequestVersionShipmentSaveModel): Observable<any> {
        return this.http.post<any>(`api:///serviceRequest/RemoveServiceRequestVersionShipment/`, body);
    }

    sendServiceRequestToTerminal(serviceRequestId: number): Observable<any> {
        return this.http.post(`api:///ServiceRequest/SendServiceRequestToTerminal/${serviceRequestId}`, null);
    }

    getShipmentLinkedForMotOnServiceRequest(
        serviceRequestId: number,
        serviceRequestVersionShipmentId: number
    ): Observable<any> {
        return this.http.get(
            `api:///ServiceRequest/GetShipmentLinkedForMotOnServiceRequest/${serviceRequestId}/${serviceRequestVersionShipmentId}`
        );
    }

    getShipmentLinkedForContainerOnServiceRequest(
        serviceRequestId: number,
        serviceRequestVersionShipmentId: number
    ): Observable<any> {
        return this.http.get(
            `api:///ServiceRequest/GetShipmentLinkedForContainerOnServiceRequest/${serviceRequestId}/${serviceRequestVersionShipmentId}`
        );
    }

    getShipmentsNotLinkedToMOTstatus(serviceRequestId: number): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetShipmentsNotLinkedToMOTstatus/${serviceRequestId}`);
    }

    getMOTNotLinkedToShipmentStatus(serviceRequestId: number): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetMOTNotLinkedToShipmentStatus/${serviceRequestId}`);
    }

    getOrganizationShippingLinesForServiceRequestVersion(serviceRequestVersionId: number): Observable<any> {
        return this.http.get<any>(
            `api:///serviceRequest/GetOrganizationShippingLinesForServiceRequestVersion/${serviceRequestVersionId}`
        );
    }

    getContainerTypeForISOType(isoTypeId: string): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetContainerTypeForISOType/${isoTypeId}`);
    }

    approveServiceRequest(dto: any): Observable<any> {
        return this.http.post(`api:///ServiceRequest/AcceptServiceRequest`, dto);
    }

    getUndeclaredCargoMeasuresForApproval(serviceRequestId: number): Observable<string[]> {
        return this.http.get<string[]>(
            `api:///ServiceRequest/GetUndeclaredCargoMeasuresForApproval/${serviceRequestId}`
        );
    }

    declineServiceRequest(dto: any): Observable<any> {
        return this.http.post(`api:///ServiceRequest/DeclineServiceRequest`, dto);
    }

    cancelServiceRequest(dto: any): Observable<any> {
        return this.http.post(`api:///ServiceRequest/CancelServiceRequest`, dto);
    }

    completeServiceRequest(dto: any): Observable<any> {
        return this.http.post(`api:///ServiceRequest/CompleteServiceRequest`, dto);
    }

    removeContainers(data: any): Observable<any> {
        return this.http.post(`api:///serviceRequest/RemoveServiceRequestVersionContainers`, data);
    }

    saveContainer(body: any): Observable<any> {
        return this.http.post('api:///serviceRequest/SaveServiceRequestVersionContainer', body);
    }

    getShippingLines(): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetShippingLinesForContainers`);
    }

    holdServiceRequest(dto: any): Observable<any> {
        return this.http.post(`api:///ServiceRequest/HoldServiceRequest`, dto);
    }

    holdServiceRequests(dto: any): Observable<any> {
        return this.http.post(`api:///ServiceRequest/HoldServiceRequests`, dto);
    }

    holdServiceRequestItems(dto: any): Observable<any> {
        return this.http.post(`api:///ServiceRequest/HoldServiceRequestItems`, dto);
    }

    createServiceRequestCustomInspection(dto: any): Observable<any> {
        return this.http.post(`api:///ServiceRequest/CreateServiceRequestCustomInspection`, dto);
    }

    createServiceRequestCustomInspectionFromContainers(dto: any): Observable<any> {
        return this.http.post(`api:///ServiceRequest/CreateServiceRequestCustomInspectionFromContainers`, dto);
    }

    createServiceRequestCustomInspectionFromShipmentItems(dto: any): Observable<any> {
        return this.http.post(`api:///ServiceRequest/CreateServiceRequestCustomInspectionFromShipmentItems`, dto);
    }

    getOrganizationsForServiceRequestMOTWagon(): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetOrganizationsForServiceRequestMOTWagon`);
    }

    getCargoHoldTypesForUser(): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetCargoHoldTypesForUser`);
    }

    getRealizationData(serviceRequestId: any): Observable<any> {
        return this.http.get(`api:///ServiceRequest/GetRealizationData/${serviceRequestId}`);
    }

    getServiceRequestVersionContainer(containerId: any): Observable<any> {
        return this.http.get(`api:///ServiceRequest/GetServiceRequestVersionContainer/${containerId}`);
    }

    deleteServiceRequest(serviceRequestId: number): Observable<any> {
        return this.http.post(`api:///ServiceRequest/DeleteServiceRequest/${serviceRequestId}`, null);
    }

    getEditDataForServiceRequest(serviceRequestId: any): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetEditDataForServiceRequest/${serviceRequestId}`);
    }

    getEditDataForVehicle(serviceRequestVersionVehicleId: any): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetEditDataForVehicle/${serviceRequestVersionVehicleId}`);
    }

    getServiceRequestVehicleLinkedUnitOptions(filter: {
        serviceRequestId: number;
        currentVehicleId?: number;
    }): Observable<any> {
        return this.http.post<any>(`api:///ServiceRequest/GetServiceRequestVehicleLinkedUnitOptions`, filter);
    }
    getEditDataForPassenger(serviceRequestVersionPassengerId: any): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetEditDataForPassenger/${serviceRequestVersionPassengerId}`);
    }

    editServiceRequestAdditionalData(model: any): Observable<any> {
        return this.http.post<any>('api:///serviceRequest/EditServiceRequestAdditionalData', model);
    }

    uploadExcelOnServiceRequest(model: any): Observable<any> {
        return this.http.post<any>(`api:///ServiceRequest/UploadExcelOnServiceRequest`, model);
    }

    linkContainersToMots(model: any) {
        return this.http.post<any>(`api:///ServiceRequest/LinkServiceRequestVersionContainersToMots`, model);
    }

    linkShipmentUnitsToMot(model: any) {
        return this.http.post<any>(`api:///ServiceRequest/LinkServiceRequestVersionShipmentUnitsToMot`, model);
    }

    checkLinkedLockedOrCompletedGeneralAct(serviceRequestId: number): Observable<boolean> {
        return this.http.get<boolean>(
            `api:///ServiceRequest/CheckLinkedLockedOrCompletedGeneralAct/${serviceRequestId}`
        );
    }

    revertCompletedStatus(model: any): Observable<any> {
        return this.http.post<any>(`api:///ServiceRequest/RevertCompletedStatus`, model);
    }

    revertDeclinedStatus(serviceRequestId: number): Observable<any> {
        return this.http.post<any>(`api:///ServiceRequest/RevertDeclinedStatus/${serviceRequestId}`, null);
    }

    getAllContainerIds(filter: any): Observable<any> {
        return this.http.post<any>(`api:///serviceRequest/GetAllContainerIds`, filter);
    }

    getAllItemIds(filter: any): Observable<any> {
        return this.http.post<any>(`api:///serviceRequest/GetAllItemIds`, filter);
    }

    getContainerVisitIds(filter: any): Observable<any> {
        return this.http.post('api:///ServiceRequest/GetAllContainerVisitIds', filter);
    }

    getContainerList(filter: any): Observable<any> {
        return this.http.post('api:///ServiceRequest/GetContainerList', filter);
    }

    getVesselVisitsForFromBulPCS(filter: any): Observable<any> {
        return this.http.post(`api:///ServiceRequest/GetVesselVisitsForFromBulPCS`, filter);
    }

    getContainersFromBulPCS(filter: any): Observable<any> {
        return this.http.post('api:///ServiceRequest/GetContainersFromBulPCS', filter);
    }

    getVehiclesFromBulPCS(filter: any): Observable<any> {
        return this.http.post('api:///ServiceRequest/GetVehiclesFromBulPCS', filter);
    }

    getShipmentUnitsFromBulPCS(filter: any): Observable<any> {
        return this.http.post('api:///ServiceRequest/GetShipmentUnitsFromBulPCS', filter);
    }

    getShipmentUnitsFromBulPCSOptions(serviceRequestId: number): Observable<any> {
        return this.http.get(`api:///ServiceRequest/GetShipmentUnitsFromBulPCSOptions/${serviceRequestId}`);
    }

    addContainersFromBulPCS(body: any) {
        return this.http.post('api:///serviceRequest/AddContainersFromBulPCS', body);
    }

    addVehiclesFromBulPCS(body: any) {
        return this.http.post('api:///serviceRequest/AddVehiclesFromBulPCS', body);
    }

    addShipmentUnitsFromBulPCS(body: any) {
        return this.http.post('api:///serviceRequest/AddShipmentUnitsFromBulPCS', body);
    }

    getServiceRequestVersionShipmentList(filter: any): Observable<any> {
        return this.http.post('api:///ServiceRequest/GetServiceRequestVersionShipmentList', filter);
    }

    getServiceRequestVersionShipmentDetailsList(filter: any): Observable<any> {
        return this.http.post('api:///ServiceRequest/GetServiceRequestVersionShipmentDetailsList', filter);
    }

    getCargoUnitManagementOptions(serviceRequestId: number): Observable<any> {
        return this.http.get(`api:///ServiceRequest/GetCargoUnitManagementOptions/${serviceRequestId}`);
    }

    getLandServiceRequestVersionShipmentDetailsList(filter: any): Observable<any> {
        return this.http.post('api:///ServiceRequest/GetLandServiceRequestVersionShipmentDetailsList', filter);
    }

    getServiceRequestVehiclesList(filter: any): Observable<any> {
        return this.http.post('api:///ServiceRequest/GetServiceRequestVehiclesList', filter);
    }

    getServiceRequestPassengersList(filter: any): Observable<any> {
        return this.http.post('api:///ServiceRequest/GetServiceRequestPassengersList', filter);
    }

    getContainersSummary(serviceRequestId: number): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetContainersSummary/${serviceRequestId}`);
    }

    getVehiclesSummary(serviceRequestId: number): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetVehiclesSummary/${serviceRequestId}`);
    }

    addContainerMove(body: any): Observable<any> {
        return this.http.post<any>(`api:///serviceRequest/AddContainerMove`, body);
    }

    getLinesForContainers(agentId: number, cargoManifestId?: number, serviceRequestId?: number): Observable<any> {
        return this.http.get<any>(
            `api:///serviceRequest/GetLinesForContainers/${agentId ?? 0}/${cargoManifestId ?? 0}/${
                serviceRequestId ?? 0
            }`
        );
    }

    getServiceRequestAdditionalDataChanges(serviceRequestId: number): Observable<any> {
        return this.http.get<any>(`api:///serviceRequest/GetServiceRequestAdditionalDataChanges/${serviceRequestId}`);
    }

    getVehicleVisitList(filter: any) {
        return this.http.post('api:///VehicleVisit/GetVehicleVisitList', filter);
    }

    getVehicleVisit(id: number) {
        return this.http.get<any>(`api:///VehicleVisit/GetVehicleVisit/${id}`);
    }

    getServiceRequestVehiclesForPassengers(serviceRequestId: number) {
        return this.http.get<any>(`api:///serviceRequest/GetVehiclesForPassenger/${serviceRequestId}`);
    }

    getServiceRequests(vehicleVisitId: number) {
        return this.http.get<any>(`api:///VehicleVisit/GetServiceRequests/${vehicleVisitId}`);
    }

    getVehicleMoves(vehicleVisitId: number) {
        return this.http.get<any>(`api:///VehicleVisit/GetVehicleMoves/${vehicleVisitId}`);
    }

    getSealsForContainer(serviceRequestVersionContainerId: number) {
        return this.http.get<any>(`api:///ServiceRequest/GetSealsForContainer/${serviceRequestVersionContainerId}`);
    }

    confirmVehicleMoves(dto: any): Observable<any> {
        return this.http.post(`api:///ServiceRequest/ConfirmVehicleMoves`, dto);
    }

    cancelVehicleMoves(dto: any): Observable<any> {
        return this.http.post(`api:///ServiceRequest/CancelVehicleMoves`, dto);
    }

    deleteVehicleMoves(dto: any): Observable<any> {
        return this.http.post(`api:///ServiceRequest/DeleteVehicleMoves`, dto);
    }

    saveVehicle(model: any) {
        return this.http.post<any>(`api:///ServiceRequest/SaveServiceRequestVersionVehicle`, model);
    }

    savePassenger(model: any) {
        return this.http.post<any>(`api:///ServiceRequest/SaveServiceRequestVersionPassenger`, model);
    }

    removeVehicles(model: any) {
        return this.http.post<boolean>(`api:///ServiceRequest/RemoveServiceRequestVersionVehicles`, model);
    }

    removePassengers(model: any) {
        return this.http.post<any>(`api:///ServiceRequest/RemoveServiceRequestVersionPassengers`, model);
    }

    saveServiceRequestVersionShipmentUnit(model: any) {
        return this.http.post<any>(`api:///ServiceRequest/SaveServiceRequestVersionShipmentUnit`, model);
    }

    removeServiceRequestVersionShipmentUnits(model: any) {
        return this.http.post<any>(`api:///ServiceRequest/RemoveServiceRequestVersionShipmentUnits`, model);
    }

    addPassengersFromVessel(model: any) {
        return this.http.post<any>(`api:///ServiceRequest/AddPassengersFromVessel`, model);
    }

    isButtonVisible(
        action: string,
        namespace: string,
        shipmentTypeId: string,
        serviceRequestStatusId: string,
        user: UserModel
    ): boolean {
        if (!user) return false;
        if (user.hasRole('Administrator')) return true;
        if (!user.hasPermission(`CargoOperations.${namespace}.${action}`)) return false;

        // Additional logic for shipment type and service request status
        switch (action) {
            case 'ConfirmMove':
                return (
                    shipmentTypeId !== 'NONCNTC' &&
                    (serviceRequestStatusId === 'APT' || serviceRequestStatusId === 'INP')
                );

            case 'DeleteMove':
                return (
                    shipmentTypeId !== 'NONCNTC' &&
                    (serviceRequestStatusId === 'APT' || serviceRequestStatusId === 'INP')
                );

            case 'CancelMove':
                return (
                    shipmentTypeId !== 'NONCNTC' &&
                    (serviceRequestStatusId === 'APT' ||
                        serviceRequestStatusId === 'INP' ||
                        serviceRequestStatusId === 'WAT')
                );

            default:
                return false;
        }
    }

    isButtonDisabled(itemsGridView: GridDataResult, selected: any[], holdingStatus?: boolean): boolean {
        if (!itemsGridView || !selected?.length || (holdingStatus !== undefined && !holdingStatus)) return true;

        return itemsGridView.data?.some(
            (x) =>
                selected.includes(x.id) &&
                ['LTP', 'CAN'].includes(
                    x.containerVisitStatus || x.serviceRequestVersionContainer?.containerVisitStatus
                )
        );
    }

    addUnnamedContainer(model: any) {
        return this.http.post<any>(`api:///ServiceRequest/AddUnnamedContainer`, model);
    }

    getShipmentsForCalculation(serviceRequestId: number) {
        return this.http.get<any>(`api:///ServiceRequest/GetShipmentsForCalculation/${serviceRequestId}`);
    }

    hasAdditionalService(additionalServices: any[], ids: string[]): boolean {
        if (!additionalServices) return false;
        return additionalServices?.some((x) => ids.includes(x.additionalServiceId)) ?? false;
    }

    editServiceRequestAdditionalService(model: any): Observable<any> {
        return this.http.post<any>('api:///serviceRequest/EditServiceRequestAdditionalService', model);
    }

    getWarehouserPerSystemBasedOnPermission(namespace: TosNamespace): Observable<any> {
        return this.http.post<any>(`api:///serviceRequest/GetWarehouserPerSystemBasedOnPermission`, { namespace });
    }

    getCustomsWarehouseForMrn(mrnId: number, organizationId: number): Observable<any> {
        return this.http.get<any>(`api:///ServiceRequest/GetCustomsWarehouseForMrn/${mrnId}/${organizationId}`);
    }

    getCustomsWarehouses(filter: any): Observable<any> {
        return this.http.post('api:///ServiceRequest/GetFormattedCustomsWarehouseOptions', filter);
    }

    getShipmentTypeIconAndTooltip(
        shipmentTypeId: string,
        shipmentTypeName: string
    ): { icon: IconProp; tooltip: string } {
        switch (shipmentTypeId) {
            case 'FULLCNT':
                return { icon: ['custom', 'full-container'] as any, tooltip: shipmentTypeName };
            case 'EMPTYCNT':
                return { icon: ['custom', 'empty-container'] as any, tooltip: shipmentTypeName };
            case 'RORO':
                return { icon: ['fas', 'car'], tooltip: shipmentTypeName };
            case 'NONCNTC':
                return { icon: ['fas', 'cubes'], tooltip: shipmentTypeName };
            default:
                return null;
        }
    }

    getMovePerformedClass(vehicle: any): string {
        if (vehicle.moveCanceled) {
            return 'bg-canceled';
        }
        if (vehicle.movePerformed) {
            return 'bg-realized';
        }
        return '';
    }

    getAdrTooltip(adr: any): string {
        if (!adr) return '';

        let title = '';

        if (adr.unNumberId) {
            title += `${this.translateService.instant(marker('UN Nbr.'))}: ${adr.unNumberId}; `;
        }
        if (adr.class) {
            title += `${this.translateService.instant(marker('Class'))}: ${adr.class}; `;
        }
        if (adr.classificationCode) {
            title += `${this.translateService.instant(marker('Classification Code'))}: ${adr.classificationCode}; `;
        }
        if (adr.packingGroup) {
            title += `${this.translateService.instant(marker('Packing Group'))}: ${adr.packingGroup}; `;
        }
        if (adr.labels) {
            title += `${this.translateService.instant(marker('Labels'))}: ${adr.labels}; `;
        }
        if (adr.tunnelRestrictionCode) {
            title += `${this.translateService.instant(marker('Tunnel Code'))}: ${adr.tunnelRestrictionCode}; `;
        }
        if (adr.hazardIdentificationNumber) {
            title += `${this.translateService.instant(marker('HIN (Hazard Id Number)'))}: ${
                adr.hazardIdentificationNumber
            }; `;
        }

        return title;
    }
}
